<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import StatusTag from 'dashboard/components-next/tag/StatusTag.vue';
import { formatCurrency } from '../utils/crmPageUtils';
import {
  houseAddressLine,
  houseDetailsLine,
  isAgentVisit,
  lastVisitPrice,
  visitCode,
  visitDateTimeLabel,
  visitHouse,
} from '../utils/visitorInfoPresenter';

const props = defineProps({
  variant: {
    type: String,
    default: 'last',
    validator: value => ['last', 'next'].includes(value),
  },
  visits: { type: Array, default: () => [] },
  businessContext: { type: String, default: 'for_rent' },
  loading: { type: Boolean, default: false },
  title: { type: String, default: '' },
  icon: { type: String, default: '' },
  emptyTitle: { type: String, default: '' },
  emptyDescription: { type: String, default: '' },
});

const emit = defineEmits([
  'open-visit',
  'send-offer',
  'view-listing',
  'schedule',
]);

const { t } = useI18n();

const isLast = computed(() => props.variant === 'last');
const isNext = computed(() => props.variant === 'next');

// O cartão de últimas visitas se oculta por completo quando não há visitas;
// o cartão de próximas visitas sempre renderiza para mostrar o estado vazio.
const isVisible = computed(
  () => isNext.value || props.loading || props.visits.length > 0
);

const priceLabel = visit =>
  formatCurrency(lastVisitPrice(visit, props.businessContext)) ||
  t('LEADS.VISITOR_INFO.LAST_VISITS.NO_PRICE');

const offerLinkSent = visit =>
  Boolean(visit?.offer_link_sent || visit?.offerLinkSent);

const offerButtonLabel = visit =>
  offerLinkSent(visit)
    ? 'Reenviar proposta'
    : t('LEADS.VISITOR_INFO.LAST_VISITS.SEND_OFFER');
</script>

<template>
  <div>
    <LaisCard
      v-if="isVisible"
      :title="title"
      :prefix-icon="icon"
      density="tight"
    >
      <div v-if="loading" class="grid gap-3">
        <span
          class="w-full animate-pulse rounded bg-lais-bg-muted"
          :class="isLast ? 'h-24' : 'h-16'"
        />
        <span
          class="w-full animate-pulse rounded bg-lais-bg-muted"
          :class="isLast ? 'h-24' : 'h-16'"
        />
      </div>

      <!-- variant: last -->
      <div v-else-if="isLast" class="grid gap-3">
        <article
          v-for="visit in visits"
          :key="visit.id"
          class="rounded-lais-sm border border-lais-border-soft px-3 py-3"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="font-semibold text-lais-text">
                {{ priceLabel(visit) }}
              </p>
              <p class="mt-1 text-lais-body-sm text-lais-text-muted">
                {{ visitDateTimeLabel(visit) }}
              </p>
              <p
                v-if="visitCode(visit)"
                class="mt-1 text-[12px] font-semibold text-lais-text-muted"
              >
                {{ visitCode(visit) }}
              </p>
            </div>
            <StatusTag
              v-if="isAgentVisit(visit)"
              :label="t('LEADS.VISITOR_INFO.LAST_VISITS.AGENT_VISIT')"
              variant="info"
              size="xs"
            />
          </div>
          <p class="mt-3 text-lais-body-sm text-lais-text">
            {{ houseAddressLine(visitHouse(visit)) }}
          </p>
          <p class="mt-1 text-lais-body-sm text-lais-text-muted">
            {{ houseDetailsLine(visitHouse(visit)) }}
          </p>
          <div class="mt-3 flex flex-wrap items-center gap-3">
            <button
              v-if="isAgentVisit(visit)"
              type="button"
              class="inline-flex items-center gap-1 text-lais-label-sm font-semibold text-lais-primary hover:text-lais-primary-600"
              @click="emit('open-visit', visit)"
            >
              <span
                class="i-lucide-external-link size-3.5"
                aria-hidden="true"
              />
              {{ t('LEADS.VISITOR_INFO.LAST_VISITS.DETAILS') }}
            </button>
            <button
              v-if="isAgentVisit(visit)"
              type="button"
              class="inline-flex items-center gap-1 text-lais-label-sm font-semibold text-lais-primary hover:text-lais-primary-600"
              @click="emit('send-offer', visit)"
            >
              <span class="i-lucide-send size-3.5" aria-hidden="true" />
              {{ offerButtonLabel(visit) }}
            </button>
            <button
              v-else
              type="button"
              class="inline-flex items-center gap-1 text-lais-label-sm font-semibold text-lais-primary hover:text-lais-primary-600"
              @click="emit('view-listing', visitHouse(visit))"
            >
              <span class="i-lucide-home size-3.5" aria-hidden="true" />
              {{ t('LEADS.VISITOR_INFO.LAST_VISITS.VIEW_LISTING') }}
            </button>
          </div>
        </article>
      </div>

      <!-- variant: next -->
      <div v-else-if="visits.length" class="grid gap-3">
        <article
          v-for="visit in visits"
          :key="visit.id"
          class="rounded-lais-sm border border-lais-border-soft px-3 py-3"
        >
          <p class="text-lais-label-sm font-semibold text-lais-text">
            {{ visitDateTimeLabel(visit) }}
          </p>
          <p class="mt-1 text-lais-body-sm text-lais-text-muted">
            {{ houseAddressLine(visitHouse(visit)) }}
          </p>
          <button
            type="button"
            class="mt-3 inline-flex items-center gap-1 text-lais-label-sm font-semibold text-lais-primary hover:text-lais-primary-600"
            @click="emit('open-visit', visit)"
          >
            <span class="i-lucide-external-link size-3.5" aria-hidden="true" />
            {{ t('LEADS.VISITOR_INFO.NEXT_VISITS.DETAILS') }}
            <span class="sr-only">{{ visitCode(visit) }}</span>
          </button>
        </article>
      </div>
      <div
        v-else
        class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-3"
      >
        <p class="font-semibold text-lais-text">
          {{ emptyTitle }}
        </p>
        <p class="mt-1 text-lais-body-sm text-lais-text-muted">
          {{ emptyDescription }}
        </p>
        <button
          type="button"
          class="mt-3 inline-flex items-center gap-1 text-lais-label-sm font-semibold text-lais-primary hover:text-lais-primary-600"
          @click="emit('schedule')"
        >
          <span class="i-lucide-calendar-plus size-3.5" aria-hidden="true" />
          {{ t('LEADS.VISITOR_INFO.NEXT_VISITS.SCHEDULE') }}
        </button>
      </div>
    </LaisCard>
  </div>
</template>
